import _, {first, keyBy, maxBy} from 'lodash';
import {createSelector} from 'reselect';

import {onlyItem} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {hasPermission, isTeamAdmin} from '../../../helpers/models/accessHelpers';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {TeamModel} from '../../../models/api/teamModel';
import {AppState} from '../../appState';
import {getAccess, getRoles} from '../../data/dataSelectors';
import {getUser, getUserNamespace} from '../teammates/teammatesSelectors';

/** Get the map of all teams. */
export function getTeams(state: AppState) {
  return state.data.teams;
}

/** Get a flat list of all teams, sorted by name. */
export const getFlatTeams = createSelector(getTeams, (teams) =>
  _(teams)
    .values()
    .compact()
    .sortBy((t) => t.name.toLowerCase())
    .value(),
);

/** Get a map of namespace to team. */
export const getTeamsByNamespace = createSelector(getFlatTeams, (teams) => keyBy(teams, (t) => t.namespace));

/** Get a flat list of the teams that our user is part of. */
export const getUserTeams = createSelector([getUser, getFlatTeams], (user, teams) =>
  teams.filter((t) => {
    const teamAccess = user.access[t.namespace];
    return teamAccess && teamAccess.roles.length > 0;
  }),
);

/** Get a list of the team ids that our user is part of. */
export const getUserTeamIds = createSelector(getUserTeams, (teams) => teams.map((t) => t.id));

export const getFirstTeamUserIsPartOf = createSelector([getUserTeams], (teams) => first(teams));

export const getOnlyTeamUserIsPartOf = createSelector([getUserTeams], (teams) => onlyItem(teams));

export const getTeamsUserIsAdminOf = createSelector([getAccess, getUserTeams], (access, teams) =>
  teams.filter((t) => isTeamAdmin(t.namespace, access)),
);

/** Tries to get the first team the user is an admin of. */
export const getFirstTeamUserIsAdminOf = createSelector([getTeamsUserIsAdminOf], (teams) => teams[0]);

/** Get the team that was most recently updated. Returns undefined if there are no teams. */
export const getLastUpdatedTeam = createSelector([getFlatTeams], (teams) => maxBy(teams, (t) => t.updatedAt));

/**
 * Retrieve teams for this user which are currently in a Team Trial.
 */
const getUserTeamTrials = createSelector(getUserTeams, (userTeams) =>
  userTeams.filter((team) => team.preferences.isTrial && team.preferences.trialEndDate),
);

/**
 * Determine if this user belongs to only Team Trials.
 * NOTE: If this teammate belongs to a single non-trial team, they are considered a full user.
 */
const getIsUserInTeamTrial = createSelector(
  [getUserTeams, getUserTeamTrials],
  (userTeams, userTeamTrials) => userTeamTrials.length > 0 && userTeamTrials.length === userTeams.length,
);

/**
 * Check if this user belongs only to expired Team Trials, to revoke access.
 * NOTE: They should still be able to login, but can no longer send messages.
 */
export const getHasUserTeamTrialExpired = createSelector(
  [getIsUserInTeamTrial, getUserTeamTrials],
  (isUserInTeamTrial, userTeamTrials) => {
    if (!isUserInTeamTrial) {
      return false;
    }

    return userTeamTrials.every(computeHasTeamTrialExpired);
  },
);

export function makeUserIsAdminForTeam(team: TeamModel | undefined) {
  return createSelector(getTeamsUserIsAdminOf, (teams) => Boolean(teams.find((t) => t.id === team?.id)));
}

export function makeUserIsAdminForTeamNamespace(namespace: string) {
  return createSelector(getAccess, (access) => isTeamAdmin(namespace, access));
}

interface LimitedTeamSetting {
  permission: PermissionsEnum;
  path: string;
}
// Even if our user isn't a team/global admin, they might have access to some parts of the settings thanks to special roles.
// This array defines what paths in the settings could be accessed and what permission the user requires to access them.
const limitedTeamSettings: ReadonlyArray<LimitedTeamSetting> = [
  {
    permission: PermissionsEnum.TEAMMATE_UPDATE,
    path: `teammates`,
  },
  {
    permission: PermissionsEnum.INBOX_UPDATE,
    path: `inboxes`,
  },
  {
    permission: PermissionsEnum.CUSTOM_VIEW_EDIT,
    path: `custom-views`,
  },
  {
    permission: PermissionsEnum.RULE_UPDATE,
    path: `rules`,
  },
  {
    permission: PermissionsEnum.CANNED_ANSWER_UPDATE,
    path: `answers`,
  },
  {
    permission: PermissionsEnum.SIGNATURE_UPDATE,
    path: `signatures`,
  },
  {
    permission: PermissionsEnum.TAG_UPDATE,
    path: `tags`,
  },
  {
    permission: PermissionsEnum.ROLE_UPDATE,
    path: `roles`,
  },
];

export const getFirstTeamWithLimitedSettingsAccess = createSelector(
  [getUserTeams, getAccess, getRoles, getUserNamespace],
  (teams, access, roles, userNamespace) => {
    // 1a. Grab all the teams the user has access to.
    const team = _(teams)
      // 1b. Sort them by name to be consistent with the display.
      .sortBy((t) => t.name)
      // 1c. Keep only the teams in which our user has _some_ access.
      .filter((t) =>
        Boolean(
          limitedTeamSettings.find((teamSetting) =>
            hasPermission(roles, access, t.namespace, userNamespace, teamSetting.permission),
          ),
        ),
      )
      // 1d. Grab the first item.
      .first();

    if (!team) {
      return undefined;
    }

    // 2. Return the namespace and the first setting our user can access.
    return {
      namespace: team.namespace,
      limitedSettings: limitedTeamSettings.find((teamSetting) =>
        hasPermission(roles, access, team.namespace, userNamespace, teamSetting.permission),
      ),
    };
  },
);

export const getAvailableUserNamespaces = createSelector([getUser, getUserTeams], (user, userTeams) => [
  user.namespace,
  ...userTeams.map((t) => t.namespace),
]);

/*
 * Helpers.
 */

function computeHasTeamTrialExpired(team: TeamModel): boolean {
  const {trialEndDate} = team.preferences;

  if (!trialEndDate) {
    return false;
  }

  const now = new Date().getTime();

  return now > trialEndDate;
}
